import { Controller } from "@hotwired/stimulus"
import { apiGet, t } from "../api_helper"
import { isOfflineError, showOfflinePlaceholder } from "../lib/offline_ui"

export default class extends Controller {
  static targets = ["content"]

  connect() {
    this.data_ = null
    this.load()
  }

  async load() {
    try {
      const data = await apiGet("/api/referrals")
      this.data_ = data
      this.render(data)
    } catch (e) {
      if (isOfflineError(e)) { showOfflinePlaceholder(this.contentTarget, () => this.load()); return }
      console.error("Referrals load error:", e)
      this.contentTarget.innerHTML = `<div class="text-center py-8" style="color: rgb(148,163,184);">${t("referrals.error") || "Failed to load referrals"}</div>`
    }
  }

  render(data) {
    const referralLink = data.referral_link || ""
    const referralCode = data.referral_code || ""
    const count = data.referrals_count ?? data.referral_count ?? 0
    const weeksEarned = data.weeks_earned ?? 0
    const referrals = data.referrals || []

    let referralListHtml = ""
    if (referrals.length > 0) {
      referralListHtml = `
        <div class="mt-4 p-4 rounded-xl" style="background-color: rgb(248,250,252);">
          <p class="text-sm font-semibold mb-3">${t("referrals.your_referrals") || "Your referrals"}</p>
          <div class="space-y-0">
            ${referrals.map((r, i) => {
              const date = new Date(r.joined_at).toLocaleDateString(
                window.APP_LOCALE === "ru" ? "ru-RU" : "en-US",
                { day: "numeric", month: "short", year: "numeric" }
              )
              const border = i < referrals.length - 1
                ? "border-bottom: 1px solid rgb(241,245,249);"
                : ""
              return `
                <div class="flex items-center justify-between py-2.5" style="${border}">
                  <span class="text-sm font-medium">${this.escapeHtml(r.name)}</span>
                  <span class="text-xs" style="color: rgb(148,163,184);">${date}</span>
                </div>
              `
            }).join("")}
          </div>
        </div>
      `
    }

    this.contentTarget.innerHTML = `
      <div class="p-5 rounded-xl text-center" style="background-color: rgb(248,250,252);">
        <p class="text-sm mb-1" style="color: rgb(148,163,184);">${t("referrals.your_code") || "Your referral code"}</p>
        <p class="text-2xl font-bold font-mono tracking-wider mb-4">${this.escapeHtml(referralCode) || "---"}</p>
        <div class="flex gap-2 justify-center">
          <button data-action="pwa-referrals#copyLink"
                  class="px-5 py-2.5 rounded-xl text-white text-sm font-medium active:scale-95 transition-transform"
                  style="background-color: rgb(99,102,241);"
                  data-pwa-referrals-target="copyBtn">
            ${t("referrals.copy_link") || "Copy link"}
          </button>
          <button data-action="pwa-referrals#share"
                  class="px-5 py-2.5 rounded-xl text-white text-sm font-medium active:scale-95 transition-transform"
                  style="background-color: rgb(99,102,241); opacity: 0.85;">
            ${t("referrals.share") || "Share"}
          </button>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3 mt-4">
        <div class="p-4 rounded-xl text-center" style="background-color: rgb(248,250,252);">
          <p class="text-2xl font-bold">${count}</p>
          <p class="text-xs mt-1" style="color: rgb(148,163,184);">${t("referrals.total_referrals") || "Total referrals"}</p>
        </div>
        <div class="p-4 rounded-xl text-center" style="background-color: rgb(248,250,252);">
          <p class="text-2xl font-bold">${weeksEarned}</p>
          <p class="text-xs mt-1" style="color: rgb(148,163,184);">${t("referrals.weeks_earned") || "Weeks earned"}</p>
        </div>
      </div>

      ${referralListHtml}
    `
  }

  copyLink() {
    const link = this.data_?.referral_link
    if (!link) return

    navigator.clipboard.writeText(link).then(() => {
      if (this.hasCopyBtnTarget) {
        const btn = this.copyBtnTarget
        const original = btn.textContent
        btn.textContent = t("referrals.copied") || "Copied!"
        setTimeout(() => { btn.textContent = original }, 2000)
      }
    }).catch(() => {
      const el = document.createElement("textarea")
      el.value = link
      el.style.position = "fixed"
      el.style.opacity = "0"
      document.body.appendChild(el)
      el.select()
      document.execCommand("copy")
      document.body.removeChild(el)
    })
  }

  share() {
    const link = this.data_?.referral_link
    if (!link) return

    const inviteText = t("referrals.invite_text") ||
      "Join me on Coinka — the smartest finance tracker! Use my link to get a bonus:"

    if (navigator.share) {
      navigator.share({
        title: "Coinka",
        text: inviteText,
        url: link
      }).catch(() => {
        this.copyLink()
      })
    } else {
      navigator.clipboard.writeText(`${inviteText}\n${link}`).then(() => {
        alert(t("referrals.link_copied") || "Link copied to clipboard!")
      }).catch(() => {
        this.copyLink()
      })
    }
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
